import { useCallback, useEffect } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { theme, Icon, Text, Button, styles as baseStyles } from '@socrait/react';

import { trackEvent } from '~/gtag';
import { ErrorBox } from '~/components/ErrorBox';
import { Loading } from '~/components/Loading';
import { useViewStudentDataScreensFeature, useViewWeeklyInsightsFeature } from '~/featureFlags';
import { useReviewScorecards, useReviewActionItems, useWeeklyInsights } from '~/services/review';
import { useLogoutMutation } from '~/services/useLogoutMutation';
import { useUserProfile } from '~/services/useUserProfileQuery';
import { Period, SessionStatus } from '~/services/types';
import { getFormattedDate } from '~/utils/date';

import { ReviewActionItems } from './ReviewActionItems';
import { ReviewScoreCards } from './ReviewScoreCards';
import { Reminders, sortReminders } from './ui/Reminders';

const isToday = (date: Date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export type ReviewPeriodProps = {
  activePeriod: Period;
  date: Date;
  onSessionStatusChange: (status?: SessionStatus) => void;
  onRosterPress: () => void;
  setDate: (date: Date) => void;
};

export function ReviewPeriod({ activePeriod, date, onSessionStatusChange, onRosterPress, setDate }: ReviewPeriodProps) {
  const isViewWeeklyInsightsEnabled = useViewWeeklyInsightsFeature();
  const isViewStudentDataScreensEnabled = useViewStudentDataScreensFeature();
  const {
    data: scorecardData,
    error: scorecardError,
    status: scorecardStatus,
  } = useReviewScorecards({ date, periodId: activePeriod.id });
  const {
    data: actionItemsData,
    status: actionItemsStatus,
    error: actionItemsError,
  } = useReviewActionItems({
    date,
    periodId: activePeriod.id,
  });
  const { data: weeklyInsightsData, status: weeklyInsightsStatus } = useWeeklyInsights({
    date,
    periodId: activePeriod.id,
  });

  const { mutate: logout } = useLogoutMutation();
  const { user } = useUserProfile();

  useEffect(() => {
    if (scorecardData?.session?.status || actionItemsData?.session?.status) {
      onSessionStatusChange((scorecardData?.session?.status || actionItemsData?.session?.status)!);
    }
  }, [scorecardData?.session?.status, actionItemsData?.session?.status, onSessionStatusChange]);

  const hasPraiseOrWarningItems = Boolean(
    scorecardData?.scorecards?.praises?.length || scorecardData?.scorecards?.warnings?.length,
  );

  const handlePrevDay = useCallback(() => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() - 1);
    trackEvent('day_prev');
    setDate(currentDate);
  }, [date, setDate]);

  const handleNextDay = useCallback(() => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() + 1);
    trackEvent('day_next');
    setDate(currentDate);
  }, [date, setDate]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const remindersData = sortReminders(actionItemsData?.actionItems?.filter((item) => item.type === 'reminders') || []);
  const displayedReminders = isViewWeeklyInsightsEnabled
    ? remindersData
    : remindersData.filter((item) => !item.insightId);

  return (
    <View style={styles.outerContainer}>
      <View style={styles.leftContainer}>
        <View style={styles.classInfoRow}>
          <Text style={styles.classInfo}>{activePeriod?.name || 'Class Summary'}</Text>
          {isViewStudentDataScreensEnabled && (
            <Button
              icon="group"
              onPress={onRosterPress}
              containerStyle={styles.rosterButton}
              telemetryLabel="roster-button"
            />
          )}
        </View>
        <View style={styles.dateContainer}>
          <Pressable onPress={handlePrevDay}>
            <Icon type="leftArrow" />
          </Pressable>
          <Text style={styles.date}>{getFormattedDate(date)}</Text>
          <Pressable onPress={handleNextDay} disabled={isToday(date)}>
            <Icon type="rightArrow" fill={isToday(date) ? theme.colors.gray : theme.colors.navy} />
          </Pressable>
        </View>
        {scorecardStatus === 'pending' ? (
          <Loading backgroundColor={theme.colors.transparent} />
        ) : scorecardError ? (
          <ErrorBox />
        ) : (
          <ReviewScoreCards scorecardData={scorecardData} />
        )}
        <View style={styles.userInfoContainer}>
          <Text style={styles.userInfoText}>{user?.email}</Text>
          <Pressable onPress={handleLogout}>
            <Text style={[styles.userInfoText, styles.logoutText]}>Log Out</Text>
          </Pressable>
        </View>
      </View>
      <View style={styles.rightContainer}>
        <ReviewActionItems
          date={date}
          hasPraiseOrWarningItems={hasPraiseOrWarningItems}
          actionItemsData={actionItemsData}
          actionItemsQueryStatus={actionItemsStatus}
          actionItemsError={actionItemsError}
          period={activePeriod}
          weeklyInsightsData={weeklyInsightsData}
          weeklyInsightsQueryStatus={weeklyInsightsStatus}
        />
        <Reminders remindersData={displayedReminders} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'row',
    backgroundColor: theme.colors.lightGray,
    position: 'relative',
  },
  leftContainer: {
    width: 370,
    height: '100%',
    maxHeight: '100%',
    backgroundColor: theme.colors.white,
    gap: theme.spacing.xlarge,
    paddingHorizontal: theme.spacing.xlarge,
    paddingVertical: theme.spacing.xlarge + theme.spacing.medium,
    overflowY: 'auto',
  },
  rightContainer: {
    height: '100%',
    flex: 1,
    paddingHorizontal: theme.spacing.xxlarge,
    paddingVertical: theme.spacing.xxlarge,
    backgroundColor: theme.colors.lightGray,
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xxlarge,
    overflowY: 'auto',
  },
  classInfoRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  classInfo: {
    ...baseStyles.title,
    margin: 0,
    flex: 1,
  },
  rosterButton: {
    flexShrink: 0,
    width: 40,
    height: 40,
  },
  logoutText: {
    fontSize: theme.fontSizes.xsmall,
    fontWeight: theme.fontWeights.bold,
    color: theme.colors.primaryPurple,
  },
  dateContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.xlarge,
    marginVertical: theme.spacing.xlarge,
  },
  date: {
    fontSize: theme.fontSizes.medium,
    fontFamily: theme.fonts.medium,
    fontWeight: theme.fontWeights.medium,
  },
  userInfoContainer: {
    marginTop: 'auto',
    paddingTop: theme.spacing.medium,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  userInfoText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.regular,
  },
});
